/**
 * Prose text of a project file for whole-project scans (#250): entity
 * occurrences, project search, Database display names and peek bodies.
 *
 * Markdown files scan as-is. `.zwdoc` files scan their plain-text projection,
 * so offsets are projection offsets (what `scrollToHeading` expects for
 * `.zwdoc`) and locations are paragraphs rather than lines.
 */

import { isJust, just, nothing, type Maybe } from "../maybe";
import { isZwDocText, parseZwDoc } from "./format";
import {
  blockIndexAt,
  blockText,
  projectPlainText,
  type PlainTextProjection,
} from "./plainText";

export type ScanText =
  | { kind: "markdown"; text: string }
  | { kind: "zwdoc"; projection: PlainTextProjection };

const EMPTY_PROJECTION: PlainTextProjection = {
  text: "",
  runs: [],
  blocks: [],
};

/** An unreadable `.zwdoc` scans as empty rather than as raw JSON. */
export function scanTextOf(raw: string): ScanText {
  if (isZwDocText(raw) === false) return { kind: "markdown", text: raw };
  const parsed = parseZwDoc(raw);
  return {
    kind: "zwdoc",
    projection: parsed.ok
      ? projectPlainText(parsed.value.doc)
      : EMPTY_PROJECTION,
  };
}

/** Where a hit sits: Markdown line or `.zwdoc` paragraph (both 1-based). */
export type ScanLocation = {
  unit: "line" | "paragraph";
  number: number;
  /** The line / paragraph text, trimmed, line breaks folded to spaces. */
  snippet: string;
};

/** Paragraph location for a projection offset. */
export function paragraphLocation(
  projection: PlainTextProjection,
  offset: number,
): ScanLocation {
  const index = blockIndexAt(projection, offset);
  return {
    unit: "paragraph",
    number: index + 1,
    snippet: blockText(projection, index).replace(/\s+/g, " ").trim(),
  };
}

/** Text of the first level-1 heading with any text. */
export function firstTitle(projection: PlainTextProjection): Maybe<string> {
  for (const [index, block] of projection.blocks.entries()) {
    if (isJust(block.headingLevel) === false || block.headingLevel.value !== 1)
      continue;
    const title = blockText(projection, index).replace(/\s+/g, " ").trim();
    if (title !== "") return just(title);
  }
  return nothing;
}

/** Every ASCII punctuation character may be backslash-escaped (CommonMark). */
function escapeMarkdown(text: string): string {
  return text.replace(/[!-/:-@[-`{-~]/g, "\\$&");
}

/**
 * Light Markdown for the entity peek card: headings keep their level, other
 * blocks become plain paragraphs, and the text can't be read as Markdown
 * syntax. Empty blocks are dropped.
 */
export function projectionPeekMarkdown(
  projection: PlainTextProjection,
): string {
  const parts: string[] = [];
  for (const [index, block] of projection.blocks.entries()) {
    const text = blockText(projection, index).trim();
    if (text === "") continue;
    const escaped = escapeMarkdown(text);
    parts.push(
      isJust(block.headingLevel)
        ? `${"#".repeat(block.headingLevel.value)} ${escaped.replace(/\n/g, " ")}`
        : escaped,
    );
  }
  return parts.join("\n\n");
}
